/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable react/no-danger */
import WarningAltFilled16 from '@carbon/icons-react/es/warning--alt--filled/16';
import { ICellRendererParams } from 'ag-grid-community';
import { Space, Typography, Tooltip } from 'antd';
import { memoize } from 'lodash';
import React, { FC } from 'react';
import styled from 'styled-components';
import { FieldTypeDisplay } from 'api/field-sections/types';
import { MemberType, Team } from 'api/models';
import { StatusUpdateStatuses } from 'api/status-updates';
import type { FieldOptionBase, StatusFieldOption } from 'api/tasks';
import { Checkbox as CheckboxAtom } from 'components/atoms/checkbox';
import { RichTextEditor } from 'components/atoms/rich-text-editor';
import { UserTag } from 'components/atoms/select';
import { Tag } from 'components/atoms/tag';
import { PlaceholderAvatar } from 'components/molecules/placeholder-avatar';
import { TeamOption, UserOption } from 'components/molecules/squad-picker';
import { getStatusData } from 'components/organisms/projects/project-list/constants';
import { activeUsersAndTeamsFromIndexedB } from 'queries/members';
import { ProjectFieldsName } from 'routes/projects/list-v2/fields';
import type { Field } from 'stores/fields';
import { byAssigneeValue } from 'utils/comparators/by-assignee-value';
import { byFullName } from 'utils/comparators/by-full-name';
import { byLabel } from 'utils/comparators/by-label';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';
import { tryGetTextFromHTML } from 'utils/getTextFromHTML';
import { ProjectFinancialsContractTypeOptions } from 'utils/project-fields/use-project-native-fields-as-filterable-fields';
import { unreachable } from 'utils/unreachable';
import { UserFieldsName } from 'utils/user-fields';
import { NoValue, TaskFieldValue } from '../field-value';
import { SelectFieldOption } from '../field-value/option';
import {
  AssigneeValue,
  FieldType,
  FilterableField,
  MultiSelectField,
  SingleSelectField,
  SingleUserField,
  SourceType,
  UserFieldMetadata,
} from '../fields';

const NoteWrapper = styled.div`
  width: 100%;
  cursor: pointer;
  height: inherit;
  display: flex;
  align-items: center;
  --rte-min-height: 0;
  --rte-content-padding: var(--spacing-02) var(--spacing-03) var(--spacing-03) 0;
  --rte-background: transparent;
  .ck.ck-content.ck-editor__editable.ck-editor__editable_inline {
    max-height: var(--height-lg);
    white-space: pre-wrap;
    line-height: var(--spacing-07);
    overflow: hidden;
  }
  .ck.ck-content.ck-editor__editable.ck-editor__editable_inline.ck-read-only {
    padding: var(--rte-content-padding);
  }
`;

export const NoteFieldRenderer: FC<
  ICellRendererParams & {
    field: FilterableField;
    openNote?: (field: FilterableField, data) => void;
  }
> = ({ openNote, field, data, value }) => {
  return (
    <NoteWrapper onClick={() => openNote?.(field, data)}>
      {value == null ? (
        <NoValue />
      ) : (
        <RichTextEditor data={value?.replaceAll('\n', '<br>')} readonly />
      )}
    </NoteWrapper>
  );
};

const Checkbox = styled(CheckboxAtom)`
  &,
  .ant-checkbox,
  .ant-checkbox-input {
    cursor: inherit;
  }
`;

type CurrentPhasesType = { projectPhaseName: Nullable<string> };

type Nullable<T> = T | null;

type OptionsField = SingleSelectField | MultiSelectField;

const renderBoolean = memoize((value: Nullable<boolean>, disabled = false) =>
  value === null ? (
    <NoValue />
  ) : (
    <Checkbox checked={value} disabled={disabled} />
  ),
);

const renderMultiLineTextValue = memoize((value: Nullable<string>) =>
  value == null ? (
    <NoValue />
  ) : (
    <Tooltip
      placement="bottomRight"
      title={tryGetTextFromHTML(value)}
      overlayStyle={{
        maxHeight: '256px',
        maxWidth: '336px',
        overflowY: 'auto',
      }}
    >
      <div
        className="multiline-text"
        dangerouslySetInnerHTML={{
          __html: tryGetTextFromHTML(value),
        }}
      />
    </Tooltip>
  ),
);

const renderAtRisk = memoize((value: Nullable<boolean>) =>
  value ? (
    <WarningAltFilled16 style={{ color: 'var(--error-color)' }} />
  ) : (
    <NoValue />
  ),
);

export const getRenderer = (
  field: Pick<
    Field,
    | 'type'
    | 'fieldName'
    | 'metaData'
    | 'sourceType'
    | 'isEditable'
    | 'fieldTypeDisplay'
  >,
  projectBillabilityChecker: (projectId?: number) => boolean = () => false,
) => {
  if (field.fieldName === 'atRisk' && field.sourceType === SourceType.Task) {
    return ({ value }: { value: Nullable<boolean> }) => renderAtRisk(value);
  }

  if (
    field.fieldName === 'projectPhaseId' &&
    field.sourceType === SourceType.Task
  ) {
    return ({ value }: { value: Nullable<string> }) => {
      if (!value) {
        return <NoValue />;
      }
      if (typeof value === 'number') {
        const option = (field.metaData as any)?.options?.find(
          (e) => e.value === value,
        );

        if (option) {
          return (
            <Tag
              color={getRandomColor(option?.label)}
              data-cy={`tag.${field.fieldName}.${option.label}`}
            >
              {option?.label}
            </Tag>
          );
        }
      }

      return (
        <Tag
          color={getRandomColor(value)}
          data-cy={`tag.${field.fieldName}.${value}`}
        >
          {value}
        </Tag>
      );
    };
  }

  if (field.fieldName === 'pastProjectStatus') {
    return ({ value }: { value: Nullable<string> }) => {
      if (value == null) {
        return <NoValue />;
      }
      const currentStatus = getStatusData(StatusUpdateStatuses, value);

      if (currentStatus) {
        const label =
          currentStatus?.label?.toLowerCase() === StatusUpdateStatuses.none.key
            ? 'No status'
            : currentStatus?.label;

        return (
          <Tag
            color={currentStatus?.color}
            data-cy={`tag.${field.fieldName}.${label}`}
          >
            {label}
          </Tag>
        );
      }

      return (
        <Tag
          color={getRandomColor(value)}
          data-cy={`tag.${field.fieldName}.${value}`}
        >
          {value}
        </Tag>
      );
    };
  }
  if (field.fieldName === 'sprint') {
    return ({ data }) => {
      const { currentSprint } = data;

      if (currentSprint == null || currentSprint?.sprintName == null) {
        return <NoValue />;
      }

      return (
        <Tag color={getRandomColor(currentSprint.sprintName)}>
          {currentSprint.sprintName}
        </Tag>
      );
    };
  }

  if (field.fieldName === 'associatedSprints') {
    return ({ value }) => {
      if (value == null) {
        return <NoValue />;
      }

      return (
        <Space size={8}>
          {value.map((sprint) => (
            <Tag color={getRandomColor(sprint.sprintName)}>
              {sprint.sprintName}
            </Tag>
          ))}
        </Space>
      );
    };
  }

  if (field.fieldName === 'jiraIntegration') {
    return ({ value }: { value: Nullable<string> }) => {
      if (!value) {
        return <NoValue />;
      }

      const label = value.split('/browse/');

      return (
        <Typography.Text ellipsis={{ tooltip: true }}>
          <a href={value} target="_blank" rel="noreferrer">
            {label[1] ?? value}
          </a>
        </Typography.Text>
      );
    };
  }

  if (field.fieldName === ProjectFieldsName.financialContractType) {
    return ({ data }) => {
      const selectedOption = ProjectFinancialsContractTypeOptions.find(
        ({ value }) =>
          value === data?.projectFinancialsDetailModel?.financialContractType,
      );

      return (
        <Tag color={selectedOption?.color}> {selectedOption?.label ?? '_'}</Tag>
      );
    };
  }

  const { type } = field;
  const options =
    (field as OptionsField)?.metaData?.options ?? ([] as FieldOptionBase[]);

  if (field.fieldName === 'status' && field.sourceType === SourceType.Task) {
    return TaskFieldValue(options as StatusFieldOption[]);
  }

  const metadata = field.metaData as
    | (UserFieldMetadata & { teams?: Team[] } & {
        projectPlaceholders?: { [id: number]: Team[] };
      })
    | undefined;

  const activeUsersAndTeams =
    activeUsersAndTeamsFromIndexedB.getActiveUsersAndTeams;
  const { users, teams: teamList } = activeUsersAndTeams;
  const projectPlaceholders = Object.values(
    metadata?.projectPlaceholders || {},
  ).reduce((prev, current) => {
    prev.push(...current);

    return prev;
  }, []);

  const teams = [...(teamList ?? []), ...projectPlaceholders];

  const getOption = memoize(<T extends unknown>(value: Nullable<T>) =>
    options.find((o) => o.value === value),
  );

  const renderOption = memoize(<T extends unknown>(value: Nullable<T>) => {
    const selected = getOption(value);

    if (selected == null) {
      return <NoValue />;
    }

    return (
      <SelectFieldOption
        key={selected.label}
        option={selected}
        dataCy={`tag.${field.fieldName}.${selected.label}`}
        showPosition={field.fieldTypeDisplay === FieldTypeDisplay.Level}
      />
    );
  });

  const renderMultiSelect = memoize(
    <T extends unknown>(value: Nullable<T[]>) => {
      const selected = value ?? [];

      return selected.length === 0 ? (
        <NoValue />
      ) : (
        <span className="spacer">
          {selected
            .map(getOption)
            .filter(Boolean)
            .map((x) => x!)
            .sort(byLabel)
            .map((x) => renderOption(x.value))}
        </span>
      );
    },
    (value: Nullable<number[]>) =>
      Array.from(new Set(value ?? []))
        .sort()
        .map((x) => x.toString())
        .join(','),
  );

  if (
    field.fieldName === 'currentPhases' &&
    field.sourceType === SourceType.Project
  ) {
    return ({ value }: { value: Nullable<CurrentPhasesType[] | string[]> }) => {
      if (!value || value.length === 0) {
        return <NoValue />;
      }

      // temporary fix, backend will send data for current phases in project list call
      if (typeof value[0] === 'string') {
        return renderMultiSelect(value as string[]);
      }

      return (
        <span className="spacer">
          {value
            .map((v) => getOption<string>(v.projectPhaseName))
            .filter(Boolean)
            .map((x) => x!)
            .sort(byLabel)
            .map((x) => renderOption(x.value))}
        </span>
      );
    };
  }

  const renderUser = memoize((value: Nullable<number>) => {
    if (value == null) {
      return <NoValue />;
    }
    let selected = users?.find?.((x) => x.userId === value);

    if (field.fieldName === UserFieldsName.TimeSheetApprover) {
      selected = (field as SingleUserField).metaData.users.by_user_id[value];
    }

    if (selected == null) {
      return <NoValue />;
    }
    const imageUrl = selected.photoUrl;
    const label = fullName(selected);

    return (
      <UserTag
        key={selected.userId}
        color={getRandomColor(selected.userId)}
        imageUrl={imageUrl}
        userStatus={selected?.userStatus}
        label={label}
      />
    );
  });

  const toOption = memoize(
    (value: AssigneeValue) => {
      if (value == null) {
        return null;
      }

      switch (value[0]) {
        case 'team': {
          const team = (teams ?? []).find((x) => x.teamId === value[1]);

          return team == null ? null : TeamOption(team);
        }
        case 'user': {
          const user = users?.find((u) => u.userId === value[1]);

          return user == null ? null : UserOption(user);
        }
        default:
          return unreachable(value);
      }
    },
    (value: AssigneeValue) => value.join(' '),
  );

  const renderTeam = memoize((value: Nullable<number>) => {
    if (value == null) {
      return <NoValue />;
    }
    const selected = teams.find((x) => x.teamId === value);

    if (selected == null) {
      return <NoValue />;
    }

    return (
      <UserTag
        key={selected.teamId}
        color={getRandomColor(selected.teamId)}
        label={selected.teamName}
        icon={
          <PlaceholderAvatar
            isCustomerRole={selected.teamType === MemberType.External}
            name={selected.teamName}
          />
        }
      />
    );
  });

  const renderAssignees = memoize(
    (value: Nullable<AssigneeValue[]>) => {
      if (value == null || (value ?? []).length === 0) {
        return <NoValue />;
      }

      const html = value
        .map(toOption)
        .filter(Boolean)
        .sort(byLabel)
        .filter((selected) => selected?.extra != null)
        .map((selected) =>
          selected.extra!.type === 'user'
            ? renderUser(selected.extra!.id)
            : renderTeam(selected.extra!.id),
        );

      return <span className="spacer">{html}</span>;
    },
    (value: Nullable<AssigneeValue[]>) =>
      (value ?? [])
        .filter(Boolean)
        .sort(byAssigneeValue)
        .map(AssigneeValue.toString)
        .join(','),
  );

  const renderMultiUser = memoize(
    (value: Nullable<number[]>) => {
      if ((value ?? []).length === 0) {
        return <NoValue />;
      }

      const html = users
        .sort(byFullName)
        .filter(({ userId }) => value!.includes(userId))
        .map((selected, idx) => (
          <UserTag
            key={selected?.userId ?? idx}
            color={getRandomColor(selected?.userId ?? 0)}
            imageUrl={selected?.photoUrl}
            label={fullName(selected ?? ({} as any))}
            userStatus={selected?.userStatus}
          />
        ));

      return <span className="spacer">{html}</span>;
    },
    (value: Nullable<number[]>) =>
      Array.from(new Set(value ?? []))
        .sort()
        .map((x) => x.toString())
        .join(','),
  );

  switch (type) {
    case FieldType.Estimate:
    case FieldType.Number:
    case FieldType.Date:
    case FieldType.Text:
      return undefined;
    case FieldType.MultiLineText:
      return ({ value }: { value: Nullable<string> }) =>
        renderMultiLineTextValue(value);
    case FieldType.Boolean:
      return ({ value, data }: { value: Nullable<boolean>; data: any }) => {
        if (field.fieldName !== 'defaultTimeEntryBillable') {
          return renderBoolean(value);
        }

        const projectId = data?.project;

        const isProjectNonBillable =
          projectId == null
            ? false
            : projectBillabilityChecker(projectId as number);

        return renderBoolean(
          isProjectNonBillable ? false : value,
          isProjectNonBillable,
        );
      };

    case FieldType.SingleSelect:
      return ({ value }: { value: Nullable<number> }) => renderOption(value);
    case FieldType.MultiSelect:
      return ({ value }: { value: Nullable<number[]> }) =>
        renderMultiSelect(value);
    case FieldType.SingleUser:
      return ({ value }: { value: Nullable<number> }) => renderUser(value);
    case FieldType.MultiUser:
      return ({ value }: { value: Nullable<number[]> }) =>
        renderMultiUser(value);
    case FieldType.Assignee:
      return ({ value }: { value: Nullable<AssigneeValue[]> }) =>
        renderAssignees(value);
    case FieldType.Note:
      return 'NoteFieldRenderer';
    default:
      return unreachable(type);
  }
};
